import { useQuery } from '@tanstack/react-query';
import { REMINDER_DELIVERY_SELECT, parseReminderDelivery, reminderDeliveryNote, reminderHasFallenDue } from '@cerebro-diem/core';
import type { Task } from '@cerebro-diem/core';
import { errorMessage } from '../../services/errors';
import { queryKeys } from '../../services/queries';
import { supabase } from '../../services/supabase';

/**
 * The task page's one line when its reminder never reached a device
 * (CER-129): "Reminder not sent: no device received it", or "Reminder not
 * delivered" and why. It reads the reminder's notification (kind reminder,
 * this task, fire_at = its remind_at) and its deliveries; RLS gives only the
 * signed-in user's own. Nothing for a reminder delivered, still on its way or
 * still to come; a check that failed says so.
 */
export default function ReminderDeliveryNote({ task }: { task: Task }) {
  const due = reminderHasFallenDue(task, new Date());
  const remindAt = task.remind_at ?? '';
  const query = useQuery({
    queryKey: queryKeys.reminderDelivery(task.id, remindAt),
    enabled: due,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select(REMINDER_DELIVERY_SELECT)
        .eq('kind', 'reminder')
        .eq('task_id', task.id)
        .eq('fire_at', remindAt)
        .maybeSingle();
      if (error) throw error;
      return parseReminderDelivery(data);
    },
  });

  if (!due || query.isPending) return null;
  if (query.isError) {
    return <p className="text-sm text-error">Could not check whether this reminder was delivered: {errorMessage(query.error)}</p>;
  }
  const note = reminderDeliveryNote(query.data, task);
  if (note === null) return null;
  return <p className="text-sm text-error">{note}</p>;
}
